import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Zap, Clock, CheckCircle, XCircle,
  Calendar, DoorOpen, AlertTriangle,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { INITIAL_ENTITLEMENTS, LOTS, type Entitlement, type EntitlementStatus } from '@/data/mockData';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function statusLabel(s: EntitlementStatus): string {
  const map: Record<EntitlementStatus, string> = {
    active: 'Active',
    pending_approval: 'Pending Approval',
    scheduled: 'Upcoming',
    expired: 'Expired',
    revoked: 'Revoked',
    denied: 'Denied',
  };
  return map[s];
}

function statusColor(s: EntitlementStatus): string {
  const map: Record<EntitlementStatus, string> = {
    active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    pending_approval: 'bg-amber-100 text-amber-700 border-amber-200',
    scheduled: 'bg-blue-100 text-blue-700 border-blue-200',
    expired: 'bg-gray-100 text-gray-500 border-gray-200',
    revoked: 'bg-red-100 text-red-600 border-red-200',
    denied: 'bg-red-100 text-red-600 border-red-200',
  };
  return map[s];
}

function originLabel(o: string): string {
  const map: Record<string, string> = {
    airbnb: 'Airbnb',
    hostaway: 'Hostaway',
    manual_owner: 'Manual',
    contractor: 'Contractor',
    manual_override: 'Emergency',
  };
  return map[o] ?? o;
}

function formatTs(ts: string): string {
  return new Date(ts).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatDate(ts: string): string {
  return new Date(ts).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

const AVAILABLE_DOORS = ['Main Entry', 'Garage', 'Lift Lobby', 'Level 4 Corridor', 'Rooftop Terrace', 'Gym'];

// ─── Grant Access Dialog ──────────────────────────────────────────────────────

interface GrantForm {
  guestName: string;
  guestEmail: string;
  guestPhone: string;
  lotNumber: string;
  startDate: string;
  endDate: string;
  accessType: string;
  doors: string[];
}

function GrantAccessDialog({
  open, onClose, onGrant,
}: {
  open: boolean;
  onClose: () => void;
  onGrant: (data: GrantForm) => void;
}) {
  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
  const in3Days = new Date(); in3Days.setDate(in3Days.getDate() + 3);

  const [form, setForm] = useState<GrantForm>({
    guestName: '',
    guestEmail: '',
    guestPhone: '',
    lotNumber: '12',
    startDate: tomorrow.toISOString().slice(0, 10),
    endDate: in3Days.toISOString().slice(0, 10),
    accessType: 'guest',
    doors: ['Main Entry'],
  });

  function toggleDoor(door: string) {
    setForm(f => ({
      ...f,
      doors: f.doors.includes(door)
        ? f.doors.filter(d => d !== door)
        : [...f.doors, door],
    }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.guestName || !form.guestEmail) {
      toast.error('Please fill in the guest name and email.');
      return;
    }
    if (form.doors.length === 0) {
      toast.error('Select at least one door.');
      return;
    }
    onGrant(form);
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Grant Access
          </DialogTitle>
          <DialogDescription>
            Create a new access entitlement. Requests requiring committee approval will be queued automatically.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1">
              <Label htmlFor="guestName">Guest Name *</Label>
              <Input
                id="guestName"
                placeholder="e.g. Jane Smith"
                value={form.guestName}
                onChange={e => setForm(f => ({ ...f, guestName: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="guestEmail">Email *</Label>
              <Input
                id="guestEmail"
                type="email"
                placeholder="guest@example.com"
                value={form.guestEmail}
                onChange={e => setForm(f => ({ ...f, guestEmail: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="guestPhone">Mobile</Label>
              <Input
                id="guestPhone"
                placeholder="+61 4XX XXX XXX"
                value={form.guestPhone}
                onChange={e => setForm(f => ({ ...f, guestPhone: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="lotNumber">Lot</Label>
              <Select value={form.lotNumber} onValueChange={v => setForm(f => ({ ...f, lotNumber: v }))}>
                <SelectTrigger id="lotNumber">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LOTS.map(l => (
                    <SelectItem key={l.lotNumber} value={String(l.lotNumber)}>
                      Lot {l.lotNumber} — {l.ownerName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="accessType">Access Type</Label>
              <Select value={form.accessType} onValueChange={v => setForm(f => ({ ...f, accessType: v }))}>
                <SelectTrigger id="accessType">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="guest">Short-stay Guest</SelectItem>
                  <SelectItem value="contractor">Contractor / Tradesperson</SelectItem>
                  <SelectItem value="visitor">Visitor (one-day)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="startDate">Start Date *</Label>
              <Input
                id="startDate"
                type="date"
                value={form.startDate}
                onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="endDate">End Date *</Label>
              <Input
                id="endDate"
                type="date"
                value={form.endDate}
                onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Door Access</Label>
            <div className="grid grid-cols-2 gap-2">
              {AVAILABLE_DOORS.map(door => (
                <div key={door} className="flex items-center gap-2">
                  <Checkbox
                    id={`door-${door}`}
                    checked={form.doors.includes(door)}
                    onCheckedChange={() => toggleDoor(door)}
                  />
                  <Label htmlFor={`door-${door}`} className="font-normal text-sm cursor-pointer">
                    {door}
                  </Label>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-primary text-primary-foreground hover:bg-primary/90">
              Submit Request
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Panic Protocol Dialog ────────────────────────────────────────────────────

function PanicDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [guestName, setGuestName] = useState('');
  const [hours, setHours] = useState('4');
  const [confirmed, setConfirmed] = useState(false);

  function handleGrant() {
    if (!guestName) { toast.error('Enter a guest name.'); return; }
    if (!confirmed) { toast.error('Please confirm you understand the override conditions.'); return; }
    toast.success(`Emergency access granted to ${guestName} for ${hours} hours. All committee members notified.`, {
      duration: 6000,
    });
    onClose();
    setGuestName('');
    setHours('4');
    setConfirmed(false);
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Montserrat, sans-serif' }} className="flex items-center gap-2 text-red-600">
            <Zap className="h-5 w-5" />
            Emergency Access Grant
          </DialogTitle>
          <DialogDescription>
            Bypasses the approval queue. Use only when immediate access is required.
            All activity is logged and the committee will be notified immediately.
          </DialogDescription>
        </DialogHeader>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            Access is capped at <strong>24 hours</strong> maximum. You may use this option up to <strong>3 times per month</strong>.
          </div>
        </div>
        <div className="space-y-3 pt-1">
          <div className="space-y-1">
            <Label>Guest Name *</Label>
            <Input
              placeholder="Full name"
              value={guestName}
              onChange={e => setGuestName(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>Duration</Label>
            <Select value={hours} onValueChange={setHours}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {['1', '2', '4', '8', '12', '24'].map(h => (
                  <SelectItem key={h} value={h}>{h} hour{h !== '1' ? 's' : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-start gap-2 pt-1">
            <Checkbox
              id="panicConfirm"
              checked={confirmed}
              onCheckedChange={v => setConfirmed(v === true)}
            />
            <Label htmlFor="panicConfirm" className="font-normal text-sm cursor-pointer leading-relaxed">
              I understand this override bypasses normal approval, is fully audited, and must only be used in a genuine emergency.
            </Label>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            className="bg-red-600 hover:bg-red-700 text-white"
            onClick={handleGrant}
          >
            <Zap className="h-4 w-4 mr-1" />
            Grant Emergency Access
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function OwnerDashboard() {
  const [entitlements, setEntitlements] = useState<Entitlement[]>(INITIAL_ENTITLEMENTS);
  const [showGrant, setShowGrant] = useState(false);
  const [showPanic, setShowPanic] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<EntitlementStatus | 'all'>('all');

  const myLotEntitlements = entitlements.filter(e => e.lotNumber === 12);
  const activeCount = myLotEntitlements.filter(e => e.status === 'active').length;
  const pendingCount = myLotEntitlements.filter(e => e.status === 'pending_approval').length;
  const scheduledCount = myLotEntitlements.filter(e => e.status === 'scheduled').length;

  const displayed = myLotEntitlements.filter(e =>
    selectedFilter === 'all' ? true : e.status === selectedFilter,
  );

  function handleGrant(data: GrantForm) {
    const newEnt: Entitlement = {
      id: `ent-new-${Date.now()}`,
      guestName: data.guestName,
      guestEmail: data.guestEmail,
      guestPhone: data.guestPhone,
      lotNumber: Number(data.lotNumber),
      ownerName: 'Mike Deggs',
      status: 'pending_approval',
      origin: data.accessType === 'contractor' ? 'contractor' : 'manual_owner',
      startTs: new Date(data.startDate).toISOString(),
      endTs: new Date(data.endDate).toISOString(),
      doors: data.doors,
      accessGroupId: `AG-CAN-LOT${data.lotNumber.padStart(2, '0')}-GUEST`,
      createdAt: new Date().toISOString(),
    };
    setEntitlements(prev => [newEnt, ...prev]);
    setShowGrant(false);
    toast.success(`Access request for ${data.guestName} submitted — awaiting committee approval.`);
  }

  const fadeInUp = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <Toaster richColors position="top-right" />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 style={{ fontFamily: 'Montserrat, sans-serif' }} className="text-2xl font-bold text-foreground">
            My Access Dashboard
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Lot 12 — Canopy Apartments · Mike Deggs
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="border-red-200 text-red-600 hover:bg-red-50"
            onClick={() => setShowPanic(true)}
          >
            <Zap className="h-4 w-4 mr-1.5" />
            Emergency Grant
          </Button>
          <Button
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={() => setShowGrant(true)}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Grant Access
          </Button>
        </div>
      </div>

      {/* Stat Cards */}
      <motion.div
        className="grid grid-cols-1 sm:grid-cols-3 gap-4"
        variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
        initial="hidden"
        animate="visible"
      >
        {[
          { label: 'Active', value: activeCount, icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100' },
          { label: 'Pending Approval', value: pendingCount, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100' },
          { label: 'Upcoming', value: scheduledCount, icon: Calendar, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100' },
        ].map(card => (
          <motion.div key={card.label} variants={fadeInUp}>
            <Card className={`border ${card.border}`}>
              <CardContent className="p-5 flex items-center gap-4">
                <div className={`${card.bg} ${card.color} rounded-xl p-3`}>
                  <card.icon className="h-5 w-5" />
                </div>
                <div>
                  <div className={`text-3xl font-bold ${card.color}`} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {card.value}
                  </div>
                  <div className="text-sm text-muted-foreground">{card.label}</div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      {/* Entitlements Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle style={{ fontFamily: 'Montserrat, sans-serif' }} className="text-base">
              Access Entitlements — Lot 12
            </CardTitle>
            {/* Filter pills */}
            <div className="flex gap-1.5 flex-wrap">
              {(['all', 'active', 'pending_approval', 'scheduled', 'expired', 'revoked'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setSelectedFilter(f)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                    selectedFilter === f
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-muted-foreground border-border hover:border-accent hover:text-accent'
                  }`}
                >
                  {f === 'all' ? 'All' : statusLabel(f as EntitlementStatus)}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Guest</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Access Period</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Doors</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Source</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {displayed.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center py-10 text-muted-foreground">
                        No entitlements match this filter.
                      </td>
                    </tr>
                  )}
                  {displayed.map(ent => (
                    <motion.tr
                      key={ent.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="border-b border-border/50 hover:bg-muted/30 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-full bg-accent/10 flex items-center justify-center text-accent font-semibold text-xs">
                            {ent.guestName.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div>
                            <div className="font-medium">{ent.guestName}</div>
                            <div className="text-xs text-muted-foreground">{ent.guestEmail}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs text-muted-foreground">
                          <div>{formatDate(ent.startTs)}</div>
                          <div>→ {formatDate(ent.endTs)}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {ent.doors.map(d => (
                            <span key={d} className="text-xs bg-secondary text-secondary-foreground px-1.5 py-0.5 rounded">
                              {d}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-muted-foreground">{originLabel(ent.origin)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${statusColor(ent.status)}`}>
                          {statusLabel(ent.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {ent.status === 'active' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50 text-xs h-7"
                            onClick={() => {
                              setEntitlements(prev =>
                                prev.map(e => e.id === ent.id ? { ...e, status: 'revoked' } : e),
                              );
                              toast.success(`Access revoked for ${ent.guestName}.`);
                            }}
                          >
                            <XCircle className="h-3.5 w-3.5 mr-1" />
                            Revoke
                          </Button>
                        )}
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Quick Info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="border-accent/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-accent">
              <DoorOpen className="h-4 w-4" />
              Managed Doors — Lot 12
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {['Main Entry', 'Garage', 'Lift Lobby', 'Level 4 Corridor'].map(door => (
              <div key={door} className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{door}</span>
                <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full border border-emerald-100">Online</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-amber-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-4 w-4" />
              Upcoming Check-outs
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {myLotEntitlements
              .filter(e => e.status === 'active')
              .map(e => (
                <div key={e.id} className="flex items-center gap-3 text-sm">
                  <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-semibold">
                    {e.guestName.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{e.guestName}</div>
                    <div className="text-xs text-muted-foreground">Checkout: {formatDate(e.endTs)}</div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </div>
              ))}
            {myLotEntitlements.filter(e => e.status === 'active').length === 0 && (
              <p className="text-sm text-muted-foreground">No active guests.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialogs */}
      <GrantAccessDialog open={showGrant} onClose={() => setShowGrant(false)} onGrant={handleGrant} />
      <PanicDialog open={showPanic} onClose={() => setShowPanic(false)} />
    </div>
  );
}
